import { useLocation } from 'preact-iso';
import { useEffect } from 'preact/hooks';
import useSWR from 'swr';
import { EpisodeDescription } from '../../components/episode-description';
import { EpisodeButtons } from '../../components/episode-item';
import { Icon } from '../../components/icon';
import { Link } from '../../components/link';
import TopNav from '../../components/top-nav';
import { usePodcastsStore } from '../../global-state/podcasts';
import { useSessionStore } from '../../global-state/session';
import { Episode, Podcast, PodcastId } from '../../models/podcasts';
import getAdsStatus from '../../utils/ads-status';
import { fetchEpisode } from '../../utils/api';
import { readableDuration, relativeDate } from '../../utils/format-date';
import getThumbUrl from '../../utils/get-thumb';
import { usePageTitle } from '../../utils/page-title';
import { reportSentryWarning } from '../../utils/sentry';
import style from './style.module.scss';

export default function EpisodeRoute(params: { id: string; guidHash: string }) {
  const { id: podcastId, guidHash } = params;
  const podcast: Podcast | undefined = usePodcastsStore((s) => s.podcasts[podcastId]);
  const previousRoute = useSessionStore((s) => s.previousRoute);
  const episode: Episode | undefined = podcast
    ? podcast.episodes.find((ep) => ep.guidHash === params.guidHash)
    : undefined;
  usePageTitle(episode?.title);

  // Fetch episode once to ensure we have latest version. If podcast isn't in memory, it will be fetched as well
  const { isLoading: isLoadingEpisode } = useSWR(['podcast', podcastId, guidHash], () =>
    fetchEpisode(podcastId as PodcastId, guidHash, 'view_episode')
  );

  // Redirect if episode not found
  const { route } = useLocation();
  useEffect(() => {
    if (!episode && !isLoadingEpisode) {
      reportSentryWarning(new Error('Episode not found, redirecting to podcast page'), {
        podcastId,
        guidHash,
      });
      route(`/podcast/${podcastId}`);
    }
  }, [!!episode, isLoadingEpisode]);

  if (!episode)
    return (
      <div>
        <div class="flex h-32 items-center justify-center">Loading episode...</div>
      </div>
    );

  return (
    <>
      <TopNav />
      <div class="top-section">
        {previousRoute && (
          <button
            class={`button ${style.backButton}`}
            onClick={() => {
              // Workaround for Android where for some reason, when first opening the app and navigating to
              // episode or podcast, Back button doesn't work even though history.length === 2...
              if (previousRoute === '/now') route('/now');
              else history.back();
            }}
          >
            « Back
          </button>
        )}
        {podcast && (
          <div>
            <Link
              href={`/podcast/${podcast.id}`}
              style="text-decoration: none; color: inherit"
            >
              <img
                src={getThumbUrl(podcast, 200)}
                alt=""
                width="50"
                height="50"
                style={{
                  float: 'left',
                  margin: '0 8px 1rem 0',
                }}
              />

              <p style={{ margin: '0' }}>
                <strong>{podcast.name}</strong>
                <br />
                <span style="color: #444">{podcast.episodeCount} episodes</span>
              </p>
            </Link>

            <h1 class="clear-both my-2 text-xl">{episode.title}</h1>
            <p>
              {relativeDate(episode.pubDate)}
              {episode.filesize ? (
                <span>&ensp;•&ensp;{formatBytes(episode.filesize, 0)}</span>
              ) : (
                ''
              )}
              {episode.duration ? (
                <span>&ensp;•&ensp;{readableDuration(episode.duration)}</span>
              ) : (
                ''
              )}
            </p>
            <p class="mb-1">{getAdsStatus(episode, true)}</p>
            <EpisodeButtons episode={episode} />
            {episode.omit && (
              <div class="my-4 flex items-center gap-2 rounded border border-amber-400 bg-yellow-50 p-2 px-3 text-sm">
                <Icon sprite="rss-error" class="h-5 w-5 shrink-0 text-amber-600" />
                This episode is no longer present in the podcast's RSS feed. It's
                included here b/c you interacted with it before it was removed.
              </div>
            )}
            <EpisodeDescription episode={episode} />
          </div>
        )}
      </div>
    </>
  );
}

// https://stackoverflow.com/a/18650828/1546808
function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
